import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { CoverageWarning } from '../../services/api/timeOffCoverage';
import {
  DATE_PLACEHOLDER,
  ENGLISH_LABEL_FORMAT,
  formatDayMonthLong,
  toDateParts,
} from '../../utils/dateFormat';

/**
 * MKS-361 — the soft overlap warning, shown identically to the requester (on
 * the form) and to the approver (in the queue).
 *
 * SOFT, deliberately: the same contract as MKS-326's over-balance warning
 * above it on the form. It never blocks the submit and never blocks the
 * approval — it tells both people that a role on a project will have nobody
 * on those days and lets them decide. Making it a hard stop is a separate
 * decision somebody can take later with the evidence this gives them.
 */

/**
 * "19, 20 and 21 October" — one month name, not three.
 *
 * The month name comes from {@link ENGLISH_LABEL_FORMAT} rather than the active
 * date spec (MKS-351): this sentence already read English before that ticket,
 * so it must read English in both of its release-flag branches. The ISO strings
 * are fed to `toDateParts`, which splits `YYYY-MM-DD` by hand, so the day never
 * rolls back for a viewer west of UTC.
 */
export function formatDateList(dates: string[]): string {
  const parsed = dates.map((d) => toDateParts(d));
  const labels = dates.map((iso, index) => {
    const parts = parsed[index];
    if (!parts) return DATE_PLACEHOLDER;
    const next = parsed[index + 1];
    const lastOfMonth = !next || next.month !== parts.month;
    return lastOfMonth ? formatDayMonthLong(iso, ENGLISH_LABEL_FORMAT) : String(parts.day);
  });
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]!}`;
}

interface CoverageWarningListProps {
  warnings: CoverageWarning[];
  /**
   * `requester` — "you are all away"; `approver` — "they are all away". The
   * facts are the same; only the pronoun changes.
   */
  audience: 'requester' | 'approver';
  /** Who is asking for the time off — only used in the approver wording. */
  requesterName?: string;
}

export default function CoverageWarningList({
  warnings,
  audience,
  requesterName,
}: CoverageWarningListProps) {
  if (warnings.length === 0) return null;

  const who = audience === 'requester' ? 'you' : (requesterName ?? 'they');

  return (
    <div
      role="status"
      className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800 space-y-2"
    >
      <div className="flex items-center gap-2 font-medium">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span>Nobody is left in the role on some days</span>
      </div>
      <ul className="list-disc pl-6 space-y-1">
        {warnings.map((warning) => (
          <li key={`${warning.projectId}-${warning.roleId}`}>
            <span className="font-medium">{warning.projectName}</span>
            {warning.roleName ? ` — ${warning.roleName}` : ''}: {formatDateList(warning.dates)}.{' '}
            {warning.peersAway.map((p) => p.name).join(', ')} {warning.peersAway.length === 1 ? 'is' : 'are'}{' '}
            away too, so with {who} out there is nobody in that role.
          </li>
        ))}
      </ul>
      <p>
        {audience === 'requester'
          ? 'You can still submit this — your manager or PM decides.'
          : 'You can still approve this — the warning is information, not a block.'}
      </p>
    </div>
  );
}
